import { useState } from "react";
import {
  Snowflake, Wind, BedDouble, Armchair, Satellite, Star,
  Wifi, BatteryCharging, Layers, Droplets, Cookie, Tv2, AlignJustify,
  Bus, X, Scale,
} from "lucide-react";
import "./buscompare.css";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const getSeatTypeDisplay = (bus) => {
  if (bus?.seater && bus?.sleeper) return "Seater / Sleeper";
  if (bus?.sleeper) return "Sleeper";
  if (bus?.seater) return "Seater";

  return bus?.seatType || "—";
};

const AMENITY_META = {
  wifi:     { icon: <Wifi size={13} />,            label: "WiFi" },
  charging: { icon: <BatteryCharging size={13} />, label: "Charging" },
  blanket:  { icon: <Layers size={13} />,           label: "Blanket" },
  water:    { icon: <Droplets size={13} />,         label: "Water Bottle" },
  snacks:   { icon: <Cookie size={13} />,           label: "Snacks" },
  movie:    { icon: <Tv2 size={13} />,              label: "Entertainment" },
  pillow:   { icon: <AlignJustify size={13} />,     label: "Pillow" },
};

const SECTIONS = [
  {
    id: "trip",
    label: "Trip Info",
    icon: <Bus size={14} />,
    fields: [
      { key: "operator", label: "Operator" },
      { key: "busType", label: "Bus Type" },
      { key: "departure", label: "Departs" },
      { key: "arrival", label: "Arrives" },
      { key: "duration", label: "Duration" },
    ],
  },
  {
    id: "comfort",
    label: "Seats & Comfort",
    icon: <Armchair size={14} />,
    fields: [
      { key: "isAC", label: "AC", type: "bool" },
      { key: "seatTypeDisplay", label: "Seat Type" },
      { key: "hasGPS", label: "GPS Tracking", type: "bool" },
      { key: "seatsLeft", label: "Seats Left", type: "count" },
      { key: "singleSeats", label: "Single Seats", type: "count" },
    ],
  },
  {
    id: "rating",
    label: "Rating",
    icon: <Star size={14} />,
    fields: [
      { key: "rating", label: "Rating", type: "rating" },
      { key: "totalRatings", label: "Reviews", type: "count" },
    ],
  },
  {
    id: "price",
    label: "Price",
    icon: null,
    fields: [
      { key: "price", label: "Fare", type: "price" },
    ],
  },
];

function Cell({ value, type, isBest }) {
  if (type === "bool") {
    const yes = !!value;
    return (
      <div className={`bcmp-cell ${isBest ? "bcmp-cell--best" : ""}`}>
        <span className={`bcmp-icon ${yes ? "bcmp-icon--yes" : "bcmp-icon--no"}`}>{yes ? "✓" : "✗"}</span>
        <span className={yes ? "bcmp-good" : "bcmp-bad"}>{yes ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (type === "count") {
    return (
      <div className={`bcmp-cell ${isBest ? "bcmp-cell--best" : ""}`}>
        <span>{value ?? 0}</span>
      </div>
    );
  }
  if (type === "rating") {
    return (
      <div className={`bcmp-cell ${isBest ? "bcmp-cell--best" : ""}`}>
        <span className="bcmp-rating">★ {Number(value || 0).toFixed(1)}</span>
      </div>
    );
  }
  if (type === "price") {
    return (
      <div className={`bcmp-cell bcmp-cell--price ${isBest ? "bcmp-cell--cheapest" : ""}`}>
        <span className="bcmp-price-num">{fmt(value)}</span>
        {isBest && <span className="bcmp-cheapest-tag">Cheapest ✓</span>}
      </div>
    );
  }
  return (
    <div className={`bcmp-cell ${isBest ? "bcmp-cell--best" : ""}`}>
      <span>{value || "—"}</span>
    </div>
  );
}

export default function BusCompare({ buses = [], onClose, onRemove, onBook, pageMode = false }) {
  const [highlight, setHighlight] = useState(true);
  if (!buses.length) return null;

  const prices = buses.map((b) => b.price);
  const cheapestIdx = prices.indexOf(Math.min(...prices));

  const allAmenities = Array.from(
    new Set(buses.flatMap((b) => b.amenities || []))
  );

  const getBestIdx = (field, type) => {
    if (!highlight) return -1;
    if (field === "price") return cheapestIdx;
    if (type === "bool") return buses.findIndex((b) => !!b[field]);
    if (field === "rating") {
      const r = buses.map((b) => b.rating || 0);
      return r.indexOf(Math.max(...r));
    }
    if (field === "seatsLeft" || field === "singleSeats") {
      const v = buses.map((b) => b[field] || 0);
      return v.indexOf(Math.max(...v));
    }
    return -1;
  };

  const colCount = buses.length;

  const panel = (
    <div className="bcmp-panel" style={{ "--cols": colCount }}>
      {/* Header */}
      <div className="bcmp-header">
        <div className="bcmp-header-left">
          <div className="bcmp-header-badge"><Scale size={16} /></div>
          <div>
            <div className="bcmp-header-title">Compare Buses</div>
            <div className="bcmp-header-sub">{colCount} of 3 buses selected</div>
          </div>
        </div>
        <div className="bcmp-header-right">
          <label className="bcmp-toggle">
            <input type="checkbox" checked={highlight} onChange={(e) => setHighlight(e.target.checked)} />
            <span className="bcmp-toggle-track" />
            <span className="bcmp-toggle-label">Best value</span>
          </label>
          {!pageMode && (
            <button className="bcmp-x-btn" onClick={onClose}><X size={13} /> Close</button>
          )}
        </div>
      </div>

      {/* Hero columns */}
      <div className="bcmp-grid bcmp-grid--hero">
        <div className="bcmp-label-col" />
        {buses.map((b, i) => {
          const isCheap = i === cheapestIdx;
          const discountPct = b.originalPrice > b.price
            ? Math.round(((b.originalPrice - b.price) / b.originalPrice) * 100)
            : 0;
          return (
            <div key={b.id} className={`bcmp-hero ${isCheap ? "bcmp-hero--cheap" : ""}`}>
              {isCheap && <div className="bcmp-best-ribbon">BEST PRICE</div>}
              <button className="bcmp-remove" onClick={() => onRemove?.(b.id)}><X size={12} /></button>
              <div className="bcmp-logo">{b.operator?.charAt(0) || "B"}</div>
              <div className="bcmp-operator-name">{b.operator}</div>
              <div className="bcmp-route-times">
                <span className="bcmp-t">{b.departure}</span>
                <span className="bcmp-bus-line"><Bus size={16} /></span>
                <span className="bcmp-t">{b.arrival}</span>
              </div>
              <div className="bcmp-cities">{b.departureCity?.split(",")[0]} → {b.arrivalCity?.split(",")[0]}</div>
              <div className="bcmp-badges">
                <span className={`bcmp-badge ${b.isAC ? "ac" : ""}`}>
                  {b.isAC ? <Snowflake size={10} /> : <Wind size={10} />} {b.isAC ? "AC" : "Non-AC"}
                </span>
                {b.seater && (
  <span className="bcmp-badge">
    <Armchair size={10} /> Seater
  </span>
)}

{b.sleeper && (
  <span className="bcmp-badge">
    <BedDouble size={10} /> Sleeper
  </span>
)}
              </div>
              <div className="bcmp-price-big">
                {b.originalPrice > b.price && (
                  <span className="bcmp-price-orig">{fmt(b.originalPrice)}</span>
                )}
                {fmt(b.price)}
                {discountPct > 0 && <span className="bcmp-discount-tag">{discountPct}% OFF</span>}
              </div>
              <div className="bcmp-per">total fare</div>
              <button className="bcmp-book-btn" onClick={() => onBook?.(b)}>
                Select Seats
              </button>
            </div>
          );
        })}
      </div>

      {/* Sections */}
      <div className="bcmp-body">
        {SECTIONS.map((sec) => (
          <div key={sec.id} className="bcmp-section">
            <div className="bcmp-section-head">
              {sec.icon} {sec.label}
            </div>
            {sec.fields.map((field) => {
              const bestIdx = getBestIdx(field.key, field.type);
              return (
                <div key={field.key} className="bcmp-grid bcmp-grid--row">
                  <div className="bcmp-label-col">{field.label}</div>
                  {buses.map((b, i) => (
                    <Cell
                      key={`${b.id}-${field.key}`}
                      value={
  field.key === "seatTypeDisplay"
    ? getSeatTypeDisplay(b)
    : b[field.key]
}
                      type={field.type}
                      isBest={highlight && i === bestIdx}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        ))}

        {/* Amenities */}
        {allAmenities.length > 0 && (
          <div className="bcmp-section">
            <div className="bcmp-section-head">
              <Wifi size={14} /> Amenities
            </div>
            {allAmenities.map((a) => (
              <div key={a} className="bcmp-grid bcmp-grid--row">
                <div className="bcmp-label-col">
                  {AMENITY_META[a]?.icon} {AMENITY_META[a]?.label || a}
                </div>
                {buses.map((b) => {
                  const has = (b.amenities || []).includes(a);
                  return (
                    <div key={b.id} className="bcmp-cell">
                      <span className={`bcmp-icon ${has ? "bcmp-icon--yes" : "bcmp-icon--no"}`}>{has ? "✓" : "✗"}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bcmp-footer">
        <span className="bcmp-note">💡 Fares shown are per the seats you pick at checkout</span>
        <button className="bcmp-clear-btn" onClick={() => onRemove?.("all")}>Clear All</button>
      </div>
    </div>
  );

  if (pageMode) return <div className="bcmp-page-wrap">{panel}</div>;

  return (
    <div className="bcmp-overlay" onClick={onClose}>
      <div className="bcmp-sheet" onClick={(e) => e.stopPropagation()}>{panel}</div>
    </div>
  );
}

// ── Floating tray shown while the user is picking buses to compare ─────────
export function BusCompareTray({ selectedBuses, onOpen, onRemove }) {
  if (!selectedBuses?.length) return null;
  return (
    <div className="bcmp-tray">
      <div className="bcmp-tray-inner">
        <span className="bcmp-tray-label"><Scale size={13} /> {selectedBuses.length}/3</span>
        <div className="bcmp-tray-chips">
          {selectedBuses.map((b) => (
            <div key={b.id} className="bcmp-chip">
              <span className="bcmp-chip-logo">{b.operator?.charAt(0) || "B"}</span>
              <div className="bcmp-chip-info">
                <span className="bcmp-chip-name">{b.operator}</span>
                <span className="bcmp-chip-time">{b.departure} → {b.arrival}</span>
              </div>
              <span className="bcmp-chip-price">{fmt(b.price)}</span>
              <button className="bcmp-chip-rm" onClick={() => onRemove(b.id)}><X size={11} /></button>
            </div>
          ))}
        </div>
        <button
          className="bcmp-tray-btn"
          disabled={selectedBuses.length < 2}
          onClick={onOpen}
        >
          Compare Now →
        </button>
      </div>
    </div>
  );
}